import { existsSync } from "node:fs";
import { join } from "node:path";
import { isReasoningModel } from "./detect-reasoning-model-from-name.js";
import { pathExists, readJson } from "./filesystem-helpers.js";
import { getActiveFlag } from "./non-interactive-context.js";
import { confirmOrFlag } from "./prompt-or-flag.js";
// Step 4 của Phase 10 GitNexus integration — chạy `gitnexus wiki` conditional theo AI provider:
//   - LLMLite mode (env.ANTHROPIC_AUTH_TOKEN + ANTHROPIC_BASE_URL) → run wiki via NAL gateway
//   - Anthropic Direct (env.ANTHROPIC_API_KEY + ANTHROPIC_BASE_URL) → run wiki via Anthropic OpenAI-compat layer (v1.6.1)
//   - Subscription mode (no API key env) → SKIP wiki, log warning (OAuth không expose key cho GitNexus)
//
// GitNexus dùng OpenAI-compatible HTTP client (POST /v1/chat/completions).
// Anthropic API có compatibility layer tại https://api.anthropic.com/v1/ (Bearer auth) — work với
// GitNexus default --provider openai. Cần normalize base URL có /v1/ suffix cho Anthropic.
//
// Confirm prompt cost trước khi chạy (~$0.50 cho repo 100 modules, 2-5 phút).
// Verify .gitnexus/wiki/index.html tạo ra sau khi xong.
import { spawnAsyncCapture } from "./spawn-async-capture-with-timeout.js";
import { log, spinnerWithElapsed } from "./terminal-logger.js";
import { writeGitnexusConfigForWikiRun } from "./write-gitnexus-config-to-avoid-cli-arg-key-leak.js";

// Wiki gen full lần đầu có thể lâu — 15 phút timeout an toàn cho repo lớn.
const WIKI_TIMEOUT_MS = 15 * 60 * 1000;

// Fallback model cho LLMLite (last-resort nếu settings.json không có top-level "model").
// nal-claude là default an toàn cho LLMLite NAL. Thường KHÔNG dùng đến vì avatar ai setup
// luôn ghi model user đã chọn vào settings.json.model — fallback chỉ chạy khi user manual edit.
// GitNexus default = minimax/minimax-m2.5 → LLMLite key reject → MUST override.
const FALLBACK_LLMLITE_MODEL = "nal-claude";

// Fallback model cho Anthropic Direct (last-resort).
const FALLBACK_ANTHROPIC_MODEL = "claude-sonnet-4-5";

export type WikiProvider = "llmlite" | "anthropic";
export type WikiSkipReason = "subscription-mode" | "user-declined" | "fail";

export interface GitnexusWikiResult {
  ran: boolean;
  skipped: boolean;
  reason?: WikiSkipReason;
  wikiPath?: string;
  detail?: string;
}

interface WikiCredentials {
  provider: WikiProvider;
  apiKey: string;
  baseUrl: string;
  model: string;
}

// Anthropic OpenAI-compat layer REQUIRE `/v1/` suffix trong base URL — nếu user lưu
// "https://api.anthropic.com" (no slash) → append "/v1/" để GitNexus call đúng path.
// LLMLite endpoint base URL không cần /v1/ vì GitNexus tự append.
function normalizeAnthropicBaseUrl(rawBaseUrl: string): string {
  const cleaned = rawBaseUrl.replace(/\/+$/, ""); // strip trailing slash
  if (cleaned.endsWith("/v1")) return `${cleaned}/`;
  if (cleaned.endsWith("/v1/")) return cleaned;
  return `${cleaned}/v1/`;
}

// Đọc settings.json detect provider + credentials + model.
// Order check: Anthropic Direct (ANTHROPIC_API_KEY) > LLMLite (ANTHROPIC_AUTH_TOKEN) > null (Subscription).
//
// v1.20.0 fix: Keys giờ sống ở .envrc (direnv) thay vì settings.json. Đọc 2 nguồn:
//   1. settings.json.env (legacy, hoặc subscription-clear path)
//   2. process.env (direnv đã load .envrc vào shell env)
// Order: process.env wins if both exist (latest source of truth).
//
// v1.6.3: Model đọc từ TOP-LEVEL `settings.json.model` (Claude Code spec).
// Trả null nếu thật sự không tìm thấy key nào (Subscription mode hoặc direnv
// chưa load — caller skip wiki).
async function readSettingsForWikiCredentials(
  workspacePath: string,
): Promise<WikiCredentials | null> {
  const settingsPath = join(workspacePath, ".claude", "settings.json");
  if (!(await pathExists(settingsPath))) return null;

  try {
    const settings = await readJson<{
      env?: Record<string, unknown>;
      model?: unknown;
      avatarProvider?: unknown;
    }>(settingsPath);
    const env = settings.env || {};
    const baseUrl = typeof env.ANTHROPIC_BASE_URL === "string" ? env.ANTHROPIC_BASE_URL : null;
    if (!baseUrl) return null;

    // Model do `avatar ai setup` lưu ở top-level (Claude Code schema).
    const topLevelModel = typeof settings.model === "string" ? settings.model : "";
    const envModel = typeof env.ANTHROPIC_MODEL === "string" ? env.ANTHROPIC_MODEL : "";
    const userModel = topLevelModel.length > 0 ? topLevelModel : envModel;

    // v3: key sống trong settings.json.env (bỏ direnv/.envrc). Đọc 2 nguồn:
    //   1. process.env — nếu user/CI export thủ công.
    //   2. settings.json.env — nơi `avatar ai setup` ghi key (chmod 600, gitignored).
    const resolveKey = (envVarName: string): string | null => {
      const fromProcess = process.env[envVarName];
      if (typeof fromProcess === "string" && fromProcess.length > 0) {
        return fromProcess;
      }
      const fromSettings = env[envVarName];
      if (typeof fromSettings === "string" && fromSettings.length > 0) {
        return fromSettings;
      }
      return null;
    };

    // v1.20.1: Provider detection — 2-step:
    //   Step 1: avatarProvider field (v1.20.0 informational marker) — explicit.
    //   Step 2: baseUrl heuristic — Anthropic official endpoint vs NAL gateway.
    //
    // Tránh process.env.ANTHROPIC_API_KEY leak (vd user export key cho project A,
    // chạy `avatar` trong project B với LLMLite config — không nên dùng key project A).
    const providerHint =
      typeof settings.avatarProvider === "string" ? settings.avatarProvider : null;
    // Match hostname `api.anthropic.com` trong URL — preceded bởi protocol slash
    // hoặc domain dot. Sử dụng URL parse cho accuracy.
    let baseUrlSuggestsAnthropic = false;
    try {
      const parsed = new URL(baseUrl);
      baseUrlSuggestsAnthropic = parsed.hostname === "api.anthropic.com";
    } catch {
      // Invalid URL — treat as non-Anthropic (will fall to LLMLite path)
      baseUrlSuggestsAnthropic = false;
    }
    const effectiveProvider: "anthropic" | "llmlite" =
      providerHint === "anthropic"
        ? "anthropic"
        : providerHint === "llmlite"
          ? "llmlite"
          : baseUrlSuggestsAnthropic
            ? "anthropic"
            : "llmlite";

    if (effectiveProvider === "anthropic") {
      const anthropicKey = resolveKey("ANTHROPIC_API_KEY");
      if (anthropicKey) {
        return {
          provider: "anthropic",
          apiKey: anthropicKey,
          baseUrl: normalizeAnthropicBaseUrl(baseUrl),
          model: userModel.length > 0 ? userModel : FALLBACK_ANTHROPIC_MODEL,
        };
      }
    } else {
      const llmliteToken = resolveKey("ANTHROPIC_AUTH_TOKEN");
      if (llmliteToken) {
        return {
          provider: "llmlite",
          apiKey: llmliteToken,
          baseUrl,
          model: userModel.length > 0 ? userModel : FALLBACK_LLMLITE_MODEL,
        };
      }
    }

    // No key anywhere → Subscription mode hoặc direnv chưa load.
    return null;
  } catch {
    return null;
  }
}

// Confirm prompt UX — user biết cost + thời gian + model trước khi commit.
async function confirmWikiGeneration(baseUrl: string, model: string): Promise<boolean> {
  // Wiki tốn tiền (~$0.50/lần) ⇒ mặc định non-interactive là KHÔNG sinh, trừ khi
  // `--gitnexus-wiki yes`. Im lặng tiêu tiền của người dùng là điều cấm.
  return await confirmOrFlag(
    getActiveFlag("gitnexusWiki") === undefined
      ? undefined
      : getActiveFlag("gitnexusWiki") === "yes",
    WIKI_FLAG_SPEC,
    {
      message: `Generate wiki cho workspace? (~$0.50 qua ${baseUrl} model=${model}, 2-5 phút). Continue?`,
      default: true,
    },
  );
}

// Tail N dòng cuối — dùng khi fail để debug mà không spam terminal.
function tailLines(text: string, n: number): string {
  const lines = text.split("\n");
  return lines.slice(-n).join("\n");
}

// Main entry — branch theo provider mode + run wiki nếu LLMLite/Anthropic + user confirm.
// workspacePath: nơi đọc .claude/settings.json (credentials).
// runDir: nơi chạy `gitnexus wiki .` (mặc định = workspacePath). v3 multi-repo:
//   settings ở workspace root, wiki chạy trong src/<repo> → 2 path khác nhau.
const WIKI_FLAG_SPEC = {
  flag: "--gitnexus-wiki",
  command: "add repo",
  hint: "--gitnexus-wiki yes|no",
};

export async function runGitnexusWikiConditional(
  workspacePath: string,
  runDir: string = workspacePath,
): Promise<GitnexusWikiResult> {
  // Branch 1: detect provider mode.
  const creds = await readSettingsForWikiCredentials(workspacePath);
  if (!creds) {
    log.warn(
      "Không resolve được API key cho wiki gen.\n" +
        "  Đã kiểm tra: process.env → .envrc Avatar block → settings.json.env\n" +
        "  Nguyên nhân có thể:\n" +
        "  • Subscription mode (OAuth, không có key)\n" +
        "  • Key chưa setup — chạy 'avatar ai setup' hoặc 'avatar secrets set ANTHROPIC_API_KEY'\n" +
        "  • settings.json.env.ANTHROPIC_BASE_URL bị thiếu (provider chưa configured)",
    );
    log.dim(
      "Để gen wiki sau khi đã có key, chạy manual:\n" +
        "  gitnexus wiki . --api-key <key> --base-url <url> --model <model>",
    );
    return { ran: false, skipped: true, reason: "subscription-mode" };
  }

  // Branch 2: confirm prompt cost.
  const proceed = await confirmWikiGeneration(creds.baseUrl, creds.model);
  if (!proceed) {
    log.dim(
      "User decline wiki gen — workspace OK không có wiki. Chạy `gitnexus wiki` manual sau khi cần.",
    );
    return { ran: false, skipped: true, reason: "user-declined" };
  }

  // Branch 3: run wiki với spinner + buffer stdio.
  // CRITICAL: GitNexus default --model = minimax/minimax-m2.5 → key sẽ reject. MUST override --model.
  // Provider naming: Anthropic Direct dùng OpenAI-compat layer của Anthropic API (https://api.anthropic.com/v1/)
  // → GitNexus --provider openai vẫn work với base URL đó (Anthropic ship compat endpoint).
  //
  // v1.6.4: Auto-detect reasoning models (Claude 4+, OpenAI o-series) → add --reasoning-model
  // để strip `temperature` param (API reject "temperature is deprecated for this model").
  const reasoningMode = isReasoningModel(creds.model);

  // v1.13.0 SECURITY FIX: Pre-write `~/.gitnexus/config.json` để gitnexus binary
  // tự đọc credentials, KHÔNG pass `--api-key` qua CLI args (leak qua `ps aux`).
  // Gitnexus support đọc config từ file khi vắng flag (xác minh `gitnexus wiki --help`).
  await writeGitnexusConfigForWikiRun({
    apiKey: creds.apiKey,
    baseUrl: creds.baseUrl,
    model: creds.model,
    isReasoningModel: reasoningMode,
  });

  // Args giờ KHÔNG còn --api-key. Gitnexus đọc từ ~/.gitnexus/config.json (chmod 600).
  // GIỮ --base-url/--model qua flag CÓ CHỦ ĐÍCH: chúng KHÔNG phải secret (chỉ lộ
  // LLM provider trong `ps`), và đảm bảo gitnexus dùng đúng base-url/model đã chọn
  // bất kể nó có ưu tiên config file hay không. Bỏ flag = rủi ro gitnexus fallback
  // default model (minimax) → key reject → phá wiki. Đánh đổi không đáng.
  const args = ["wiki", ".", "--base-url", creds.baseUrl, "--model", creds.model];
  if (reasoningMode) {
    args.push("--reasoning-model");
  }

  const sp = spinnerWithElapsed(
    `Generating wiki via ${creds.baseUrl} (${creds.provider}) model=${creds.model}${reasoningMode ? " [reasoning]" : ""}`,
  );
  // spawn async (không spawnSync): event loop tự do → spinner tick + Ctrl-C kill
  // được child gitnexus (tránh zombie ăn CPU/network khi user huỷ giữa 15ph).
  const result = await spawnAsyncCapture("gitnexus", args, {
    cwd: runDir,
    timeoutMs: WIKI_TIMEOUT_MS,
  });

  if (result.status !== 0 || result.timedOut) {
    const reason = result.timedOut ? "timeout" : "non-zero-exit";
    sp.fail(`Wiki gen ${reason} (exit ${result.status ?? "null"})`);
    // Dump tail 30 dòng output để debug, không spam full.
    const stderr = (result.stderr || "").trim();
    const stdout = (result.stdout || "").trim();
    if (stderr) process.stderr.write(`${tailLines(stderr, 30)}\n`);
    else if (stdout) process.stderr.write(`${tailLines(stdout, 30)}\n`);
    return {
      ran: false,
      skipped: true,
      reason: "fail",
      detail: `Wiki gen ${reason} (exit ${result.status ?? "null"})`,
    };
  }

  // Verify output tồn tại (wiki tạo trong runDir, không phải workspacePath).
  const wikiPath = join(runDir, ".gitnexus", "wiki", "index.html");
  if (!existsSync(wikiPath)) {
    sp.fail("Wiki exit 0 nhưng không thấy index.html");
    return {
      ran: false,
      skipped: true,
      reason: "fail",
      detail: `Wiki exit 0 nhưng không thấy ${wikiPath}`,
    };
  }

  sp.succeed(`Wiki ready: ${wikiPath}`);
  return { ran: true, skipped: false, wikiPath };
}
